import { useQuery } from '@tanstack/react-query';

import { callGet } from '@/api/fetcher';
import type { LanguagePreset } from '@/constants/voicePresets';
import type {
  TelnyxVoiceConfig,
  TwilioVoiceConfig,
  WebVoiceConfig,
} from '@/shared/types/integration';

export const VOICE_PRESETS_ENDPOINT = '/www/api/v1/integrations/voice/presets';

/** Voice channels the orchestrator serves presets for. */
export type VoiceChannel = 'web' | 'twilio' | 'telnyx';

/** Full voice config shape per channel — bundles diverge here (e.g. Telnyx denoiser engines). */
type ChannelVoiceConfig = {
  web: WebVoiceConfig;
  twilio: TwilioVoiceConfig;
  telnyx: TelnyxVoiceConfig;
};

/** A preset bundle is a partial voice config for a channel (language + STT/TTS/denoiser). */
export type VoicePresetBundle<C extends VoiceChannel = VoiceChannel> = Partial<
  ChannelVoiceConfig[C]
> & { language: LanguagePreset };

interface VoicePresetsResponse {
  presets: { [C in VoiceChannel]: Record<LanguagePreset, VoicePresetBundle<C>> };
}

/**
 * Fetches the canonical voice preset config bundles from the orchestrator and returns the
 * given channel's slice. Bundles diverge per channel (e.g. denoiser engines).
 * Static config — cached 10 minutes, mirrors useVoiceProviders.
 */
export const useVoicePresets = (channel: VoiceChannel | undefined) => {
  const { data, isLoading } = useQuery<VoicePresetsResponse>({
    queryKey: [VOICE_PRESETS_ENDPOINT],
    queryFn: () => callGet(VOICE_PRESETS_ENDPOINT),
    staleTime: 10 * 60 * 1000,
  });

  return { presets: channel ? data?.presets?.[channel] : undefined, isLoading };
};
